import { Box } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button, C1ButtonColorType, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle, closeFunction } from 'components/core/dialog/C1Dialog'
import { APIError } from 'components/core/errors/api'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { FC, ReactNode, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export type SimpleConfirmationDialogProps = {
    submitActionLabel?: ReactNode
    cancelActionLabel?: ReactNode
    errorLabel?: string
    title: string
    details?: ReactNode
    onConfirm?: () => Promise<void>
    onCancel?: () => void
    color?: C1ButtonColorType | 'warning'
    inProgress?: boolean
} & ClosableProps

export const SimpleConfirmationDialog: FC<SimpleConfirmationDialogProps> = ({
    submitActionLabel,
    cancelActionLabel,
    errorLabel,
    title,
    onConfirm,
    onCancel,
    details,
    inProgress,
    color = 'primary' as C1ButtonColorType,
    close,
}) => {
    const intl = useIntl()

    const [formError, setFormError] = useState<RpcError | undefined>(undefined)

    const onClose: closeFunction = async (event, reason, success): Promise<void> => {
        if (success) {
            setFormError(undefined)
            try {
                await onConfirm?.()
            } catch (error) {
                if (error instanceof RpcError) {
                    setFormError(error)
                    return
                }

                setFormError(new RpcError(intl.formatMessage({
                    id: 'component_edit_title_error_unknown',
                    defaultMessage: 'An unknown error occurred. {err}',
                }, { err: JSON.stringify(error) })))

                return
            }
            close(event, reason, success)
        } else {
            onCancel?.()
            close(event, reason, success)
        }
    }

    return (
        <C1Dialog maxWidth="sm" open onClose={(e): void => onClose(e, 'backdropClick', false)} color={color}>
            <C1DialogTitle>{title}</C1DialogTitle>
            <C1DialogContent>
                <Box mb={2}>
                    <APIError
                        err={formError}
                        title={errorLabel || intl.formatMessage({ id: 'component_edit_title_error', defaultMessage: 'Error: Failed to submit' })}
                    />
                </Box>
                {details}
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={(e): void => onClose(e, undefined, false)} autoFocus variant="outlined">
                    {cancelActionLabel || <FormattedMessage id="component_confirmation_go_back" defaultMessage="Go back" />}
                </C1Button>
                <C1LoadingButton
                    onClick={(e): void => onClose(e, undefined, true)}
                    variant="contained"
                    loading={inProgress}
                    color={color === 'error' ? 'error' : 'primary'}
                >
                    {submitActionLabel || <FormattedMessage id="component_confirmation_submit" defaultMessage="Continue" />}
                </C1LoadingButton>
            </C1DialogActions>
        </C1Dialog>
    )
}
